import { Button } from "@validereinc/common-components";
import { findIndex } from "es-toolkit/compat";
import * as PropTypes from "prop-types";
import React, { Component, ReactNode } from "react";
import FontAwesome from "react-fontawesome";
import { Table } from "react-virtualized";
import { InputIsMatched } from "../../../utils/filters";
import { SortListByType } from "../../../utils/sorters";
import Inputbox from "../../Inputs/Inputbox";
import "./CustomTable.css";

const HEIGHT_OFFSET = 50; // Account height for the things outside of the table (title, searchbox, and etc)

const HIGHLIGHTED_TABLE_ROW_COLOR = "rgba(69, 192, 193, 0.2)";

const BORDER_OFFSET = 2;

// TODO: this needs to be deprecated
class CustomTable extends Component<any, any> {
  constructor(props) {
    super(props);
    this.state = {
      updateTable: false,
      highlightIndex: -1,
      filterValue: "",
      sortDirection: "asc",
      sortBy: null,
    };

    this.handleSearch = this.handleSearch.bind(this);
    this.getRowColor = this.getRowColor.bind(this);
    this.headerRenderer = this.headerRenderer.bind(this);
    this.getSortedList = this.getSortedList.bind(this);
    this.getRowClassName = this.getRowClassName.bind(this);
  }

  componentDidMount() {
    this.setState({
      sortBy: this.props.defaultSortBy,
      sortDirection: this.props.defaultSortDirection,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      const { highlightRow, list } = this.props;
      const sortedList = this.getSortedList(list);

      if (highlightRow) {
        const highlightIndex = findIndex(sortedList, { id: highlightRow.id });

        this.setState({
          highlightIndex: highlightIndex,
        });
      }
    }
  }

  noRowsRenderer() {
    return <div className="noDataRow">No data available</div>;
  }

  getRowColor = (index) => {
    const { highlightIndex } = this.state;

    if (highlightIndex !== -1 && index === highlightIndex) {
      return { backgroundColor: HIGHLIGHTED_TABLE_ROW_COLOR };
    }

    return { backgroundColor: "#fff" };
  };

  handleSearch(event) {
    const value = event.target.value;
    this.setState({
      filterValue: value,
    });
  }

  getFilterList(list, filterBy, value) {
    return list.filter((row) => InputIsMatched(value, row[filterBy]));
  }

  headerRenderer({ dataKey, label, disableSort }) {
    const { sortBy, sortDirection } = this.state;
    return (
      <div
        onClick={() => {
          disableSort ? null : this.setSortingOrder(dataKey);
        }}
        className={disableSort ? "disablePointerEvent" : "clickable"}
      >
        {sortBy === dataKey ? (
          <FontAwesome
            name={sortDirection === "asc" ? "sort-asc" : "sort-desc"}
            className="icon"
          />
        ) : null}

        {label}
      </div>
    );
  }

  setSortingOrder(columnKey) {
    const { sortDirection, sortBy } = this.state;

    if (columnKey !== sortBy) {
      this.setState({
        sortBy: columnKey,
        sortDirection: "asc",
      });
    } else {
      const nextSortDirection = sortDirection === "asc" ? "desc" : "asc";
      this.setState({
        sortDirection: nextSortDirection,
      });
    }
  }

  csvDownload(list) {
    this.props.csvDownload(list);
  }

  getSortedList(list) {
    const { customSort } = this.props;
    const { sortBy, sortDirection } = this.state;

    if (!sortBy) {
      return list;
    }
    if (customSort?.[sortBy]) {
      return sortDirection === "asc"
        ? customSort[sortBy](list, sortBy)
        : customSort[sortBy](list, sortBy).reverse();
    } else {
      return SortListByType(list, sortBy, sortDirection);
    }
  }

  getRowClassName(row) {
    const { rowClassName } = this.props;
    if (rowClassName) {
      if (typeof rowClassName === "string") {
        return rowClassName;
      } else {
        return row ? rowClassName(row) : "";
      }
    }
  }

  getTableHeight(title, filterPillbox, csvDownload, filterKey, filterRow) {
    if (title || filterPillbox || csvDownload || filterKey || filterRow) {
      return this.props.height - HEIGHT_OFFSET;
    } else {
      return this.props.height;
    }
  }

  render() {
    const {
      title,
      list,
      children,
      width,
      rowHeight,
      filterKey,
      filterTitle,
      csvDownload,
      cellClassName,
      addButtonName,
      filterRow,
      filterPillbox,
    } = this.props;

    const { highlightIndex, updateTable, filterValue } = this.state;

    const TableHeight = this.getTableHeight(
      title,
      filterPillbox,
      csvDownload,
      filterKey,
      filterRow
    );

    const filteredList = this.getFilterList(list, filterKey, filterValue);

    const sortedlist = this.getSortedList(filteredList);

    let childrenWithProps: ReactNode = null;

    if (children) {
      childrenWithProps = React.Children.map(children, (child: any) => {
        if (child) {
          return React.cloneElement(child, {
            headerRenderer: this.headerRenderer,
          });
        }
        return null;
      });
    }

    return sortedlist && Array.isArray(sortedlist) ? (
      <div
        className={`customTable ${this.props.className}`}
        style={{ width: width }}
      >
        {title || this.props.onAddClick ? (
          <div
            className="customTable__titleContainer"
            style={{ marginBottom: filterKey ? undefined : "20px" }}
          >
            {title && <div className="customTable__title">{title}</div>}

            {this.props.onAddClick && (
              <Button
                variant="primary"
                className="addButton"
                onClick={this.props.onAddClick}
              >
                add {`${addButtonName || title}`}
              </Button>
            )}
          </div>
        ) : null}

        <div className="customTable__actionContainer">
          {(filterKey || filterRow) && (
            <div className="customTable__filterRow">
              {this.props.filterKey && (
                <Inputbox
                  className="inputbox-compress"
                  placeholder={` ${filterTitle ? filterTitle : "Search"}`}
                  onChange={this.handleSearch}
                />
              )}

              {this.props.filterRow}
            </div>
          )}

          <div className="datatable__actionRow">
            {csvDownload && (
              <Button
                className="csvDownloadButton pull-right"
                onClick={() => this.csvDownload(sortedlist)}
                disabled={sortedlist.length === 0}
                style={{ textTransform: "uppercase" }}
              >
                csv
              </Button>
            )}
          </div>
        </div>

        {filterPillbox}

        <Table
          width={width - BORDER_OFFSET}
          height={TableHeight}
          headerHeight={40}
          rowCount={sortedlist.length}
          rowGetter={({ index }) => sortedlist[index]}
          rowHeight={rowHeight ? rowHeight : 40}
          noRowsRenderer={this.noRowsRenderer}
          rowStyle={({ index }) => this.getRowColor(index)}
          scrollToIndex={highlightIndex}
          updateTable={updateTable}
          gridClassName={cellClassName}
          onRowClick={this.props.onRowClick}
          rowClassName={({ index }) => this.getRowClassName(sortedlist[index])}
        >
          {childrenWithProps}
        </Table>
      </div>
    ) : (
      <div>Table error</div>
    );
  }
}

(CustomTable as any).propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  list: PropTypes.array,
  width: PropTypes.number,
  height: PropTypes.number,
  highlightRow: PropTypes.object,
  filterRow: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  filterPillbox: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  defaultSortBy: PropTypes.string,
  defaultSortDirection: PropTypes.string,
  filterKey: PropTypes.string,
  filterTitle: PropTypes.string,
  cellClassName: PropTypes.string,
  rowHeight: PropTypes.number,
  addButtonName: PropTypes.string,
  csvDownload: PropTypes.func,
  onAddClick: PropTypes.func,
  onRowClick: PropTypes.func,
  customSort: PropTypes.object,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

export default CustomTable;
